import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent, CartAddEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {HTMLElement | null} */
  #errorPopup = null;

  /** @type {number | undefined} */
  #errorPopupTimeout;

  connectedCallback() {
    super.connectedCallback();

    // Mostra subito la barra, sempre fissa
    this.#showStickyBar();
    this.#getTargetButton();
    this.#setupFooterOffset();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    if (this.#errorPopupTimeout) {
      clearTimeout(this.#errorPopupTimeout);
    }
  }

  #getTargetButton() {
    const productForm = this.#getProductForm();
    if (!productForm) return;
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  /* #setupFooterOffset() {
    const footer =
      document.querySelector('footer') ??
      document.querySelector('[class*="footer-group"]');

    /** @type {Element | null} *
    const perfumerSection = document.querySelector('.perfumer');

    // Nothing to track — keep bar at default position
    if (!footer && !perfumerSection) return;

    const update = () => {
      let overlapFooter = 0;
      let overlapPerfumer = 0;

      if (footer) {
        const footerRect = footer.getBoundingClientRect();
        overlapFooter = Math.max(0, window.innerHeight - footerRect.top);
      }

      if (perfumerSection) {
        const perfumerRect = perfumerSection.getBoundingClientRect();
        // The section is (partially) inside the viewport from the bottom
        if (perfumerRect.top < window.innerHeight) {
          overlapPerfumer = Math.max(0, window.innerHeight - perfumerRect.top);
        }
      }

      const bottom = Math.max(overlapFooter, overlapPerfumer);
      this.refs.stickyBar.style.setProperty('--sticky-bar-bottom', `${bottom}px`);
    };

    update();

    const opts = { passive: true, signal: this.#abortController.signal };
    window.addEventListener('scroll', update, opts);
    window.addEventListener('resize', update, opts);
  } */
  #setupFooterOffset() {
    const footer =
      document.querySelector('footer') ??
      document.querySelector('[class*="footer-group"]');

    /** @type {Element | null} */
    const perfumerSection = document.querySelector('.perfumer');

    if (!footer && !perfumerSection) return;

    let raf = null;
    let currentOffset = -1;

    const setOffset = (offset) => {
      const roundedOffset = Math.round(offset);

      if (roundedOffset === currentOffset) return;

      currentOffset = roundedOffset;

      this.refs.stickyBar.style.setProperty(
        '--sticky-bar-offset',
        `${roundedOffset}px`
      );
    };

    const update = () => {
      raf = null;

      let overlapFooter = 0;
      let overlapPerfumer = 0;

      if (footer) {
        const footerRect = footer.getBoundingClientRect();
        overlapFooter = Math.max(0, window.innerHeight - footerRect.top);
      }

      if (perfumerSection) {
        const perfumerRect = perfumerSection.getBoundingClientRect();

        if (perfumerRect.top < window.innerHeight) {
          overlapPerfumer = Math.max(0, window.innerHeight - perfumerRect.top);
        }
      }

      const offset = Math.max(overlapFooter, overlapPerfumer);

      setOffset(offset);
    };

    const requestUpdate = () => {
      if (raf) return;

      raf = requestAnimationFrame(update);
    };

    requestUpdate();

    const opts = { passive: true, signal: this.#abortController.signal };

    window.addEventListener('scroll', requestUpdate, opts);
    window.addEventListener('resize', requestUpdate, opts);

    window.lenisInstance?.on?.('scroll', requestUpdate);

    const resizeObserver = new ResizeObserver(requestUpdate);

    resizeObserver.observe(document.body);

    if (footer) resizeObserver.observe(footer);
    if (perfumerSection) resizeObserver.observe(perfumerSection);

    this.#abortController.signal.addEventListener('abort', () => {
      resizeObserver.disconnect();

      if (raf) {
        cancelAnimationFrame(raf);
        raf = null;
      }
    });
  }

  // Public action handlers
  /**
   * Add-to-cart click sulla sticky bar.
   * POST nativo a /cart/add.js + dispatch globale di CartAddEvent: il
   * cart-drawer-component[auto-open] si apre da solo, cart-icon aggiorna il
   * counter. Niente apertura drawer manuale (causava conflitti dialog mobile),
   * niente loader / fly-to-cart / popup errore custom.
   */
  handleAddToCartClick = async () => {
    const variantId = this.dataset.currentVariantId;
    if (!variantId) return;
    const quantity = this.#currentQuantity || 1;

    // Section ids da rinfrescare nel payload (cart drawer contents).
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean)
      .join(',');

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity }],
          sections: sectionIds,
        }),
      });
      const data = await response.json();
      // Errore lato Shopify (es. quantità max raggiunta): popup verde sopra
      // la sticky bar, niente drawer. Il popup è position:absolute → non
      // tocca il dialog state, quindi nessun rischio mobile come prima.
      if (data.status) {
        this.#showErrorPopup(data.description || data.message);
        return;
      }

      // Carrello aggiornato per l'a11y announcement del drawer (non bloccante).
      let cart = null;
      try {
        const cartResponse = await fetch('/cart.js');
        if (cartResponse.ok) cart = await cartResponse.json();
      } catch {}

      document.dispatchEvent(
        new CartAddEvent(cart, this.id || 'sticky-add-to-cart', {
          source: 'sticky-add-to-cart',
          // cart-icon.js fa SET (non add) quando source !== 'product-form-component',
          // quindi qui mandiamo il TOTALE del carrello, non l'incremento.
          // Fallback a `quantity` se /cart.js è fallito (preview anti-bot).
          itemCount: cart?.item_count ?? quantity,
          productId: this.dataset.productId,
          sections: data.sections,
        })
      );
    } catch (error) {
      console.error('Sticky add to cart failed:', error);
    }
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  /* #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  }; */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Forza sempre stuck=true dopo il morph
    this.refs.stickyBar.setAttribute('data-stuck', 'true');
    this.#isStuck = true;
    this.dataset.variantAvailable = variantAvailable;

    if (variant?.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache bottone
    this.#getTargetButton();

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Mostra il messaggio d'errore (es. quantità max) in un popup verde
   * sopra la sticky bar. position:absolute → non altera il layout della
   * barra. Auto-hide dopo 10s (allineato a ERROR_MESSAGE_DISPLAY_DURATION
   * del product-form nativo).
   * @param {string} message
   */
  #showErrorPopup(message) {
    if (!message) return;
    const { stickyBar } = this.refs;

    // Re-crea il popup se mancante o staccato dal DOM (può succedere se una
    // section morph esterna lo rimuove). Senza questo, la seconda chiamata
    // troverebbe un nodo orfano e il messaggio non comparirebbe.
    if (!this.#errorPopup || !this.#errorPopup.isConnected) {
      const popup = document.createElement('div');
      popup.className = 'sticky-add-to-cart__message';
      popup.setAttribute('role', 'status');
      stickyBar.appendChild(popup);
      this.#errorPopup = popup;
    }

    // Forza il restart della transition: rimuovi la classe, force reflow,
    // poi ri-aggiungi. Senza il reflow, ri-aggiungere `.is-visible` quando
    // era stata appena tolta non fa partire una nuova animazione.
    this.#errorPopup.classList.remove('is-visible');
    this.#errorPopup.textContent = message;
    void this.#errorPopup.offsetWidth;
    this.#errorPopup.classList.add('is-visible');

    if (this.#errorPopupTimeout) clearTimeout(this.#errorPopupTimeout);
    this.#errorPopupTimeout = setTimeout(() => {
      this.#errorPopup?.classList.remove('is-visible');
    }, 10000);
  }

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber, quantityDisplayHover, quantityNumberHover } =
      this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number (real label + the hover-swap clone)
    quantityNumber.textContent = this.#currentQuantity.toString();
    if (quantityNumberHover) quantityNumberHover.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    const display = available && this.#currentQuantity > 1 ? 'inline' : 'none';
    quantityDisplay.style.display = display;
    if (quantityDisplayHover) quantityDisplayHover.style.display = display;
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
